import AppLoading from "components/AppLoading/AppLoading";
import { Project } from "graphql/_Types";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  createProjectFoldersStore,
  ProjectFoldersStoreState,
} from "./ProjectFoldersProvider.helpers";
import assert from "assert";
import { ContextOutOfBoundsError } from "providers/ContextOutOfBoundsError";
import { isDefined } from "utils/isDefined";
import { useStore } from "zustand";

const ProjectFoldersContext = createContext<ProjectFoldersStore | undefined>(
  undefined,
);

export type ProjectFoldersStore = Awaited<
  ReturnType<typeof createProjectFoldersStore>
>;

interface ProjectFoldersProviderProps {
  children: ReactNode;
  projectKey: Project["key"];
}

export const ProjectFoldersProvider = ({
  children,
  projectKey,
}: ProjectFoldersProviderProps) => {
  const [store, setStore] = useState<ProjectFoldersStore>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    // Reset state
    setStore(undefined);
    setIsLoading(true);
    setError(undefined);
    // Create a new store
    createProjectFoldersStore({
      projectKey,
    })
      .then(setStore)
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, [projectKey]);

  if (error !== undefined) {
    throw error;
  }

  if (isLoading) {
    return <AppLoading />;
  }
  return (
    <ProjectFoldersContext.Provider value={store}>
      {children}
    </ProjectFoldersContext.Provider>
  );
};

/** Hook for consuming the {@link ProjectFolderContext} */
export const useProjectFolderContext = <T,>(
  selector: (state: ProjectFoldersStoreState) => T,
  equalityFn?: (a: T, b: T) => boolean,
) => {
  const store = useContext(ProjectFoldersContext);
  assert(isDefined(store), new ContextOutOfBoundsError("ProjectFolderContext"));
  return useStore(store, selector, equalityFn);
};
